import { useState } from 'react'

import { useSaveSettings, useSettings } from '../../hooks/queries'
import { BLASENFARBEN, schriftAuf, type Blasenfarbe } from '../../lib/blasenfarben'

/**
 * Die Farben der Sprechblasen — hier und nicht im Chat.
 *
 * Anders als bei der Linie auf der Karte: dort stellt man ein, was man
 * gleichzeitig ansieht. Ein Farbfeld über dem Chat läge dagegen mitten im
 * Gespräch und wäre jeden Tag im Weg, für eine Einstellung, die man einmal
 * trifft. Deshalb steht sie hier — **mit zwei Beispielblasen**, damit man das
 * Ergebnis trotzdem sofort sieht.
 *
 * **Leer heißt „wie das Erscheinungsbild".** Das ist der wichtige Zustand: eine
 * einmal gesetzte helle Farbe gälte sonst auch in der dunklen Darstellung, und
 * eine weiße Blase auf dunklem Grund wäre eine Sackgasse. Der Knopf
 * *Automatisch* führt zurück.
 */
export function ChatfarbenCard() {
  const settings = useSettings()
  const save = useSaveSettings()
  const [entwurf, setEntwurf] = useState<Partial<Record<Blasenfarbe, string>>>({})
  const [gemeldet, setGemeldet] = useState(false)

  const daten = settings.data
  // Ohne Decke ist dieses Konto keine Verwaltung -- der Server hat entschieden.
  if (!daten?.ceiling) return null

  const werte = daten.settings
  const gilt = (welche: Blasenfarbe) => entwurf[welche] ?? werte[welche] ?? ''
  const geaendert = BLASENFARBEN.some((e) => gilt(e.key) !== (werte[e.key] ?? ''))

  function setze(welche: Blasenfarbe, wert: string) {
    setGemeldet(false)
    setEntwurf((alt) => ({ ...alt, [welche]: wert }))
  }

  return (
    <div className="card card--seite">
      <h2 className="card__titel">Farbe der Sprechblasen</h2>
      <p className="card__lead" style={{ marginBottom: 10 }}>
        Gilt für alle — was du hier wählst, sehen deine Leser genauso.
      </p>

      {BLASENFARBEN.map((eintrag) => (
        <div key={eintrag.key} className="chatfarbe">
          {/* Ein natives Farbfeld: auf dem iPhone öffnet das den Farbwähler
              des Systems, und der kann mehr, als ich hier je nachbauen
              sollte. */}
          <input
            type="color"
            className="kartenregler__farbe"
            name={eintrag.key}
            aria-label={eintrag.titel}
            // Ein Farbfeld kennt kein „leer". Steht nichts, zeigt es die
            // Farbe, die gerade gilt -- und die weiß nur der Browser.
            value={gilt(eintrag.key) === '' ? '#d5f2c7' : gilt(eintrag.key)}
            onChange={(event) => setze(eintrag.key, event.target.value)}
          />
          <span className="chatfarbe__text">
            <strong>{eintrag.titel}</strong>
            <span className="chatfarbe__hinweis">{eintrag.hinweis}</span>
          </span>
          <button
            type="button"
            className="button button--outline button--small"
            disabled={gilt(eintrag.key) === ''}
            onClick={() => setze(eintrag.key, '')}
          >
            Automatisch
          </button>
        </div>
      ))}

      {/* Die Vorschau. Sie trägt die Farben als eigene Angabe und nicht über
          die Variablen am Wurzelelement: solange nicht gespeichert ist, soll
          sich nur dieses Beispiel ändern und nicht die halbe Anwendung. */}
      <div className="chatvorschau">
        <div className="bubble-row">
          <div
            className="bubble"
            style={
              gilt('chat_other_color') === ''
                ? undefined
                : {
                    background: gilt('chat_other_color'),
                    color: schriftAuf(gilt('chat_other_color')),
                  }
            }
          >
            Sind die Bilder von heute?
          </div>
        </div>
        <div className="bubble-row bubble-row--own">
          <div
            className="bubble"
            style={
              gilt('chat_own_color') === ''
                ? undefined
                : {
                    background: gilt('chat_own_color'),
                    color: schriftAuf(gilt('chat_own_color')),
                  }
            }
          >
            Ja, vom Pass — es hat den ganzen Tag geregnet.
          </div>
        </div>
      </div>

      {save.isError && (
        <p className="notice" role="alert">
          {save.error instanceof Error ? save.error.message : 'Das hat nicht geklappt.'}
        </p>
      )}
      {gemeldet && !save.isPending && (
        <p className="card__lead" style={{ color: 'var(--accent)' }}>Gespeichert.</p>
      )}

      <button
        className="button button--outline"
        type="button"
        disabled={!geaendert || save.isPending}
        onClick={() => {
          setGemeldet(false)
          save.mutate(
            {
              chat_own_color: gilt('chat_own_color'),
              chat_other_color: gilt('chat_other_color'),
            },
            {
              onSuccess: () => {
                setEntwurf({})
                setGemeldet(true)
              },
            },
          )
        }}
      >
        {save.isPending ? 'Einen Moment …' : 'Speichern'}
      </button>
    </div>
  )
}
